import { CommonModule, formatDate } from '@angular/common';
import { Component, computed, DestroyRef, inject, input, LOCALE_ID, NgZone, output, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MembershipStatus } from '@membership/membership';
import { EntitlementsService } from '@services/entitlements';
import { SubscriptionsService } from '@services/subscriptions';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { firstValueFrom, map, of, switchMap } from 'rxjs';

import { refusalReason } from '@core/models/apis';
import { AdminShortenPeriodEndResponseV1, SubscriptionStatus } from '@core/models/subscriptions';
import { environment } from '@src/environments/environment';

const SYNC_ENTITLEMENTS_REASON = "Sync entitlements to match PaymentsEngine's access end";

// The only subscription statuses PaymentsEngine accepts a period-end change against, whatever the date.
const SHORTENABLE_SUBSCRIPTION_STATUSES: ReadonlySet<SubscriptionStatus> = new Set<SubscriptionStatus>([
  'Active',
  'PastDue',
  'PendingCancellation'
]);

function isSameUtcDay(a: Date, b: Date): boolean {
  return (
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate()
  );
}

type LegOutcome = 'succeeded' | 'failed' | 'skipped';

/**
 * How AME's own expiry compares to PaymentsEngine's access end.
 *
 * `aheadOfBilling` is normal until the first renewal: AME dates each allocation from sign-up by the
 * feature's own pricing period (a year for an annual feature), while PaymentsEngine's first period
 * lasts a month. A renewal copies PaymentsEngine's period end into AME, and from then on the two match
 * exactly. So AME being later only points to a lost change when PaymentsEngine's current end is one an
 * admin shortened it to. `behindBilling` means AME is already earlier than billing, which traces to
 * something upstream of this panel (a missed renewal event, typically); `setMembershipExpiration` only
 * ever moves an expiry earlier, so a sync can't repair it, and the next renewal is what does.
 */
type EntitlementsDrift = 'aligned' | 'aheadOfBilling' | 'behindBilling';

interface SubmitResult {
  action: 'shorten' | 'sync';
  payments: LegOutcome;
  entitlements: LegOutcome;
  paymentsReason?: string | null;
  entitlementsReason?: string | null;
  committedPeriodEndUtc?: string;
  reason?: string;
  squareUpdated?: boolean;
}

@Component({
  selector: 'app-membership-expiration-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, DatePickerModule, InputTextModule, MessageModule],
  templateUrl: './membership-expiration-panel.html',
  styleUrl: './membership-expiration-panel.scss'
})
export class MembershipExpirationPanel {
  private readonly entitlementsService = inject(EntitlementsService);
  private readonly subscriptionsService = inject(SubscriptionsService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly locale = inject(LOCALE_ID);

  protected readonly MembershipStatus = MembershipStatus;
  protected readonly squareRenewalsReachThisEnvironment = environment.features.squareRenewalsReachThisEnvironment;
  protected readonly shortenedEndPassedNote =
    'The shortened period end has passed, and AccessManagement only takes an expiry in the future. The next renewal will bring entitlements back in line.';
  protected readonly entitlementsAlreadyEarlierNote =
    "AccessManagement's entitlements already expire before PaymentsEngine's access end, and it only moves an expiry earlier, so it would refuse a retry. The next renewal will bring entitlements back in line.";

  readonly membershipId = input.required<string>();
  /** Emitted once AccessManagement has accepted the new expiration. */
  readonly adjusted = output<void>();

  readonly selectedDate = signal<Date | null>(null);
  readonly reason = signal('');
  readonly shortening = signal(false);
  readonly syncing = signal(false);
  readonly retrying = signal(false);
  readonly submitting = computed(() => this.shortening() || this.syncing() || this.retrying());
  readonly result = signal<SubmitResult | null>(null);

  // Checked once a minute but only changes on a new UTC day, so minDate and everything derived from it
  // correct themselves if the panel is left open across midnight and stay the same objects otherwise:
  // the datepicker rebuilds its calendar whenever minDate's identity changes, and a rebuild detaches
  // the day cells a click may be landing on.
  private readonly now = signal(new Date(), { equal: isSameUtcDay });

  // The same reading kept to the minute, for the checks against AME's refusal of any expiry that isn't
  // in the future; `now` can lag the real time by most of a day. Each of those checks reads it last, so
  // it only triggers change detection while an unsynced shortening or a failed leg is showing.
  private readonly clock = signal(new Date());

  constructor() {
    // Outside Angular's zone: under zone.js each firing would otherwise run change detection across the
    // whole app, and the pending interval would keep the app from ever reporting itself stable. A signal
    // the timer changes still schedules change detection for whatever reads it.
    const timer = inject(NgZone).runOutsideAngular(() =>
      setInterval(() => {
        const reading = new Date();
        this.now.set(reading);
        this.clock.set(reading);
      }, 60_000)
    );
    inject(DestroyRef).onDestroy(() => clearInterval(timer));
  }

  readonly subscriptionResource = rxResource({
    params: () => this.membershipId(),
    stream: ({ params }) =>
      this.entitlementsService
        .getMembership(params)
        .pipe(
          switchMap((membership) =>
            membership.subscriptionId
              ? this.subscriptionsService
                  .getSubscription(membership.subscriptionId)
                  .pipe(map((subscription) => ({ membership, subscription })))
              : of({ membership, subscription: null })
          )
        )
  });

  private readonly currentData = computed(() =>
    this.subscriptionResource.hasValue() ? this.subscriptionResource.value() : null
  );

  private readonly currentMembership = computed(() => this.currentData()?.membership ?? null);

  private readonly currentSubscription = computed(() => this.currentData()?.subscription ?? null);

  readonly minDate = computed(() => {
    const now = this.now();
    return new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
  });

  // PaymentsEngine takes the new end as a UTC calendar day that must come before both the current period end
  // and the access end; a pending cancellation's access end can come first. Ending the range on the day before
  // the earlier end's own UTC day keeps every offered day within that, whatever time of day either end falls at.
  readonly maxDate = computed(() => {
    const subscription = this.currentSubscription();
    if (!subscription) return null;
    const end = new Date(
      Math.min(
        new Date(subscription.currentPeriodEndUtc).getTime(),
        new Date(subscription.effectiveAccessEndUtc).getTime()
      )
    );
    return new Date(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate() - 1);
  });

  // Every UTC calendar day between minDate and maxDate, inclusive. Sized from the whole-day count
  // rather than walked with a `<=` instant comparison: a cursor re-derived one setDate() at a time and
  // compared by getTime() can mis-fire in a zone where DST starts at midnight, where the local day
  // after the transition has no clean midnight instant to compare against.
  private readonly daysInRange = computed(() => {
    const min = this.minDate();
    const max = this.maxDate();
    if (!max) return [];

    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    const dayCount = Math.round((max.getTime() - min.getTime()) / millisecondsPerDay) + 1;

    return Array.from({ length: Math.max(dayCount, 0) }, (_, offset) => {
      const day = new Date(min);
      day.setDate(day.getDate() + offset);
      return day;
    });
  });

  // Square's own anchor drifts against PaymentsEngine's month arithmetic past the 28th, so the
  // engine refuses any target there; blocking those cells up front saves the round trip.
  readonly disabledDates = computed(() => this.daysInRange().filter((day) => day.getDate() >= 29));

  readonly selectableDays = computed(() => this.daysInRange().filter((day) => day.getDate() < 29));

  readonly canShortenStatus = computed(() => {
    const subscription = this.currentSubscription();
    return !!subscription && SHORTENABLE_SUBSCRIPTION_STATUSES.has(subscription.status);
  });

  readonly hasRoomToShorten = computed(() => {
    if (this.currentMembership()?.status === MembershipStatus.Cancelled) return false;
    return this.selectableDays().length > 0;
  });

  readonly entitlementsDrift = computed<EntitlementsDrift>(() => {
    const expirationUtc = this.currentMembership()?.currentExpirationUtc;
    const subscription = this.currentSubscription();
    if (!expirationUtc || !subscription) return 'aligned';

    const ameTime = new Date(expirationUtc).getTime();
    const paymentsTime = new Date(subscription.effectiveAccessEndUtc).getTime();
    if (ameTime === paymentsTime) return 'aligned';
    return ameTime > paymentsTime ? 'aheadOfBilling' : 'behindBilling';
  });

  // The end an admin shortened this period to, while AME's entitlements still expire after it: the split
  // left when that shortening's AME leg never lands. Null while a failed leg shows in the result region,
  // which already covers it: "Retry entitlements" there re-sends that same committed date and reason, so
  // offering the sync as well would give two different fixes for the one problem. Null too while a reload
  // is in flight, as for canSubmit: until it lands, both reads are from before the last write.
  private readonly unsyncedShortenedEnd = computed(() => {
    const subscription = this.currentSubscription();
    if (
      this.subscriptionResource.isLoading() ||
      !subscription?.currentPeriodEndShortenedByAdmin ||
      this.entitlementsDrift() !== 'aheadOfBilling' ||
      this.result()?.entitlements === 'failed'
    ) {
      return null;
    }
    return subscription.effectiveAccessEndUtc;
  });

  // AME refuses any expiry that isn't in the future, so the sync is offered only until that end passes;
  // after it, the panel says why there is none.
  readonly canSyncEntitlements = computed(() => {
    const end = this.unsyncedShortenedEnd();
    return !!end && this.isInTheFuture(end);
  });

  readonly unsyncedShortenedEndHasPassed = computed(() => {
    const end = this.unsyncedShortenedEnd();
    return !!end && !this.isInTheFuture(end);
  });

  // AME only ever moves an expiry earlier, and a missed renewal leaves the membership's own on the old period
  // end, so once the reload after a failed leg shows it before the committed end, AME must refuse that date.
  readonly entitlementsExpireBeforeCommittedEnd = computed(() => {
    const committed = this.result()?.committedPeriodEndUtc;
    const expiry = this.currentMembership()?.currentExpirationUtc;
    return !!committed && !!expiry && new Date(committed).getTime() > new Date(expiry).getTime();
  });

  // A retry re-sends the committed date, so it is offered only while AME can take it: not once the membership's
  // own expiry is earlier, and not once that date has passed.
  readonly canRetryEntitlements = computed(() => {
    const current = this.result();
    return (
      current?.entitlements === 'failed' &&
      !!current.committedPeriodEndUtc &&
      !this.entitlementsExpireBeforeCommittedEnd() &&
      this.isInTheFuture(current.committedPeriodEndUtc)
    );
  });

  readonly canSubmit = computed(() => {
    if (this.submitting() || this.subscriptionResource.isLoading()) return false;
    if (!this.hasRoomToShorten() || !this.canShortenStatus()) return false;
    if (this.reason().trim().length === 0) return false;
    const date = this.selectedDate();
    if (!date) return false;
    // By calendar day rather than exact instant: the picker always emits local midnight, but this
    // also has to accept whatever time-of-day a caller's own Date happens to carry.
    return this.selectableDays().some((day) => this.isSameLocalDay(day, date));
  });

  private isSameLocalDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  private isInTheFuture(utc: string): boolean {
    return new Date(utc).getTime() > this.clock().getTime();
  }

  // Scoped to this membership so two panels on the same page -- one user can hold more than one --
  // never share a control id.
  readonly expirationDateInputId = computed(() => `expiration-date-${this.membershipId()}`);
  readonly periodDriftHintId = computed(() => `expiration-date-hint-${this.membershipId()}`);
  readonly expirationReasonInputId = computed(() => `expiration-reason-${this.membershipId()}`);
  readonly reasonHintId = computed(() => `expiration-reason-hint-${this.membershipId()}`);

  confirmShorten(): void {
    const subscription = this.currentSubscription();
    const date = this.selectedDate();
    if (!subscription || !date || !this.canSubmit()) return;

    const billingNote =
      subscription.isProviderBacked && subscription.status !== 'PendingCancellation'
        ? ' Square will bill the full membership price on that date, with no credit for days already paid.'
        : '';

    this.confirmationService.confirm({
      message: `Shorten this subscription's billing period, for every membership on it, from ${formatDate(subscription.currentPeriodEndUtc, 'medium', this.locale, 'UTC')} UTC to ${formatDate(date, 'mediumDate', this.locale)} (UTC)? This takes effect immediately.${billingNote}`,
      header: 'Confirm Period Change',
      icon: 'pi pi-exclamation-triangle',
      accept: () => void this.submit()
    });
  }

  async submit(): Promise<void> {
    const subscription = this.currentSubscription();
    const date = this.selectedDate();
    if (!subscription || !date || !this.canSubmit()) return;

    this.result.set(null);
    this.shortening.set(true);
    const reason = this.reason().trim();

    try {
      let adjusted: AdminShortenPeriodEndResponseV1;
      try {
        adjusted = await firstValueFrom(
          this.subscriptionsService.shortenPeriodEnd(subscription.subscriptionId, {
            periodEndUtc: new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString(),
            reason
          })
        );
      } catch (error) {
        this.result.set({
          action: 'shorten',
          payments: 'failed',
          entitlements: 'skipped',
          paymentsReason: refusalReason(error)
        });
        return;
      }

      this.selectedDate.set(null);

      const outcome = await this.pushEntitlementsExpiration(adjusted.committedPeriodEndUtc, reason);
      this.result.set({
        action: 'shorten',
        payments: 'succeeded',
        ...outcome,
        committedPeriodEndUtc: adjusted.committedPeriodEndUtc,
        reason,
        squareUpdated: adjusted.squareUpdated
      });
      if (outcome.entitlements === 'succeeded') this.adjusted.emit();

      this.subscriptionResource.reload();
    } finally {
      this.shortening.set(false);
    }
  }

  async retryEntitlements(): Promise<void> {
    const current = this.result();
    if (
      this.submitting() ||
      // reload() does nothing while a load is in flight, so the reload a successful retry asks for would be
      // lost and the panel would settle on the read taken before the retry.
      this.subscriptionResource.isLoading() ||
      !this.canRetryEntitlements() ||
      !current?.committedPeriodEndUtc ||
      !current.reason
    ) {
      return;
    }

    this.retrying.set(true);
    try {
      const outcome = await this.pushEntitlementsExpiration(current.committedPeriodEndUtc, current.reason);
      this.result.set({ ...current, ...outcome });
      if (outcome.entitlements === 'succeeded') {
        this.subscriptionResource.reload();
        this.adjusted.emit();
      }
    } finally {
      this.retrying.set(false);
    }
  }

  // For when PaymentsEngine committed but AME never got the update applied -- most often because a
  // reload discarded the in-memory result the "Retry entitlements" action above depends on.
  async syncEntitlements(): Promise<void> {
    const subscription = this.currentSubscription();
    if (this.submitting() || !subscription || !this.canSyncEntitlements()) return;

    this.syncing.set(true);
    const reason = this.reason().trim() || SYNC_ENTITLEMENTS_REASON;
    try {
      const outcome = await this.pushEntitlementsExpiration(subscription.effectiveAccessEndUtc, reason);
      // Carries the same committedPeriodEndUtc/reason a shorten's own result does, so "Retry entitlements"
      // below can recover a failed sync exactly like it recovers a failed shorten.
      this.result.set({
        action: 'sync',
        payments: 'skipped',
        ...outcome,
        committedPeriodEndUtc: subscription.effectiveAccessEndUtc,
        reason
      });
      if (outcome.entitlements === 'succeeded') {
        this.subscriptionResource.reload();
        this.adjusted.emit();
      }
    } finally {
      this.syncing.set(false);
    }
  }

  private async pushEntitlementsExpiration(
    expirationDateUtc: string,
    reason: string
  ): Promise<Pick<SubmitResult, 'entitlements' | 'entitlementsReason'>> {
    try {
      await firstValueFrom(
        this.entitlementsService.setMembershipExpiration(this.membershipId(), { expirationDateUtc, reason })
      );
      return { entitlements: 'succeeded', entitlementsReason: null };
    } catch (error) {
      return { entitlements: 'failed', entitlementsReason: refusalReason(error) };
    }
  }
}
